import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'

import { createInstance } from '@/helpers/api/base'
import { useModal } from '@/components/Modal'
import { useNotification } from '@/ui/Notification'
import { Log } from '@/types/logs'
import { IMechanicalInformation, MechanicalInformationRequestBody } from '@/types/mechanicalInformation'
import { FormValuesType } from '../schema'
import { buildMechanicalInformationRequestBody } from '../helpers/buildMechanicalInformationRequestBody'
import { useDateStore } from '@/components/DateInput'
import { useInvalidateControllerLogsByDate } from '@/modules/CombinedLogs/api/useGetControllerLogs'
import { useGetControllerLogParams } from '@/modules/CombinedLogs/apiHelpers/useGetControllerLogParams'
import { formatMechanicalInformationRequestBody } from '../helpers/formatMechanicalInformationRequestBody'

interface Props {
    log?: Log
    callback?: (body: MechanicalInformationRequestBody) => void
}

export const useSendMechanicalInformation = ({ log, callback }: Props) => {
    const { date } = useDateStore()
    const queryClient = useQueryClient()
    const invalidateControllerLogsByDate = useInvalidateControllerLogsByDate()
    const { closeModal } = useModal()
    const { showNotification } = useNotification()
    const logParams = useGetControllerLogParams()

    const onSuccessCallback = () => {
        closeModal()
        invalidateControllerLogsByDate(date)

        queryClient.invalidateQueries({ queryKey: ['GET_VEHICLE_ALLOCATION'] })

        showNotification({ message: 'Mechanical information submitted', type: 'submit' })
    }

    const logQueryKey = ['controllerLogs', ...logParams]
    const optimisticChangesCallback = (requestBody: MechanicalInformationRequestBody, previousLogs?: Log[]) => {
        const mechanicalInfo = formatMechanicalInformationRequestBody(requestBody, log)
        closeModal()

        if (!mechanicalInfo) {
            return previousLogs
        }

        return previousLogs?.map((item) => (item.id === mechanicalInfo.logId ? { ...item, mechanicalInfo } : item))
    }

    const createMechanicalInformationMutation = useAppMutation<
        IMechanicalInformation,
        Error,
        MechanicalInformationRequestBody,
        Log[]
    >({
        mutationFn: (data) =>
            createInstance({
                url: '/mechanical-info',
                method: 'POST',
                data,
            }),
        queryKey: logQueryKey,
        optimisticChangesCallback,
        onSuccess: onSuccessCallback,
        alertOnError: true,
    })

    const updateMechanicalInformationMutation = useAppMutation<
        IMechanicalInformation,
        Error,
        { id: string; data: MechanicalInformationRequestBody },
        Log[]
    >({
        mutationFn: ({ id, data }) =>
            createInstance({
                url: `/mechanical-info/${id}`,
                method: 'PUT',
                data,
            }),
        queryKey: logQueryKey,
        optimisticChangesCallback: (
            { data }: { id: string; data: MechanicalInformationRequestBody },
            previousLogs?: Log[]
        ) => optimisticChangesCallback(data, previousLogs),
        onSuccess: onSuccessCallback,
        alertOnError: true,
    })

    const handleSendMechanicalInformation = async (values: FormValuesType) => {
        if (!log) {
            return
        }

        const requestBody = buildMechanicalInformationRequestBody({
            log,
            values,
        })

        if (callback) {
            callback(requestBody)
            closeModal()
            return
        }

        if (log.mechanicalInfo) {
            updateMechanicalInformationMutation.mutate({ id: log.mechanicalInfo.id, data: requestBody })
        } else {
            createMechanicalInformationMutation.mutate(requestBody)
        }
    }

    return {
        handleSendMechanicalInformation,
        isPending: createMechanicalInformationMutation.isPending || updateMechanicalInformationMutation.isPending,
    }
}
